import * as React from 'react';

import type {Assignable} from 'nutshell-core/types';

import {Dialog} from '../../dialog/dialog';
import {Popover} from '../../popover/popover';

import {TaskAssigneePopover} from './task-assignee-popover';
import {TaskDelayForm} from './task-delay-form';
import type {DelayOptions} from './task-delay-form';
import type {TaskRowItem} from './types';

type AssigneePopoverProps = {
    anchor: HTMLElement | null | undefined;
    assignables: Assignable[];
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onSelect: (assigneeId?: string | null | undefined) => void;
};

type UncompleteTaskProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onUncomplete: () => void;
};

type DeleteTaskProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onDelete: () => void;
    ref: HTMLElement | null | undefined;
};

type SkipStepProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onSkip: () => void;
    ref: HTMLElement | null | undefined;
};

type UnskipStepProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onUnskip: () => void;
    ref: HTMLElement | null | undefined;
};

type DelayStepProps = {
    isOpen: boolean;
    options: Array<DelayOptions>;
    duration: string;
    reason: string;
    setDuration: (duration: string) => void;
    setReason: (reason: string) => void;
    onDelay: () => void;
    onClose: () => void;
    ref: HTMLElement | null | undefined;
};

type Props = {
    task: TaskRowItem;
    checkboxRef: HTMLElement | null | undefined;
    assigneePopoverProps: AssigneePopoverProps;
    uncompleteTaskProps: UncompleteTaskProps;
    deleteTaskProps: DeleteTaskProps;
    skipStepProps: SkipStepProps;
    unskipStepProps: UnskipStepProps;
    delayStepProps: DelayStepProps;
};

export function TaskRowPopovers(props: Props) {
    const {
        task,
        assigneePopoverProps,
        uncompleteTaskProps,
        deleteTaskProps,
        skipStepProps,
        unskipStepProps,
        delayStepProps,
    } = props;

    return (
        <>
            {assigneePopoverProps.isOpen && (
                <TaskAssigneePopover
                    anchor={assigneePopoverProps.anchor}
                    assignables={assigneePopoverProps.assignables}
                    assigneeId={task.assignee && task.assignee.id}
                    setIsAssigneePopoverOpen={assigneePopoverProps.setIsOpen}
                    onSelect={assigneePopoverProps.onSelect}
                />
            )}
            {uncompleteTaskProps.isOpen && (
                <Dialog
                    anchor={props.checkboxRef}
                    question='Are you sure you want to mark this task as incomplete?'
                    primaryText='Mark incomplete'
                    tertiaryText='Cancel'
                    onPrimary={uncompleteTaskProps.onUncomplete}
                    onTertiary={() => uncompleteTaskProps.setIsOpen(false)}
                    tertiaryVariant='secondary'
                    isWarning={true}
                    customStyles={{width: '240px'}}
                />
            )}
            {deleteTaskProps.isOpen && (
                <Dialog
                    anchor={deleteTaskProps.ref}
                    question='Are you sure you want to delete this task?'
                    primaryText='Delete'
                    tertiaryText='Cancel'
                    onPrimary={() => {
                        deleteTaskProps.setIsOpen(false);
                        deleteTaskProps.onDelete();
                    }}
                    onTertiary={() => deleteTaskProps.setIsOpen(false)}
                    tertiaryVariant='secondary'
                    isWarning={true}
                    customStyles={{width: '220px'}}
                />
            )}
            {skipStepProps.isOpen && (
                <Dialog
                    anchor={skipStepProps.ref}
                    question='Are you sure you want to skip this step?'
                    primaryText='Yes'
                    tertiaryText='No'
                    onPrimary={skipStepProps.onSkip}
                    onTertiary={() => skipStepProps.setIsOpen(false)}
                    tertiaryVariant='secondary'
                    isWarning={true}
                    customStyles={{width: '200px'}}
                />
            )}
            {unskipStepProps.isOpen && (
                <Dialog
                    anchor={unskipStepProps.ref}
                    question='Are you sure you want to unskip this step?'
                    primaryText='Yes'
                    tertiaryText='No'
                    onPrimary={unskipStepProps.onUnskip}
                    onTertiary={() => unskipStepProps.setIsOpen(false)}
                    tertiaryVariant='secondary'
                    isWarning={true}
                    customStyles={{width: '200px'}}
                />
            )}
            {delayStepProps.isOpen && (
                <Popover anchor={delayStepProps.ref} location='bottom'>
                    <TaskDelayForm
                        delayOptions={delayStepProps.options}
                        onDurationChange={delayStepProps.setDuration}
                        onReasonChange={delayStepProps.setReason}
                        delayDuration={delayStepProps.duration}
                        delayReason={delayStepProps.reason}
                        onSubmit={delayStepProps.onDelay}
                        onClose={delayStepProps.onClose}
                    />
                </Popover>
            )}
        </>
    );
}
